import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

interface Props {
  src: string;
  alt: string;
  thumbSizeClass: string;
}

const PREVIEW_W = 384; // tailwind w-96
const PREVIEW_H = (PREVIEW_W * 7) / 5;
const VIEWPORT_MARGIN = 12;

interface Pos {
  top: number;
  left: number;
}

function computePos(rect: DOMRect): Pos {
  let left = rect.right + VIEWPORT_MARGIN;
  if (left + PREVIEW_W + VIEWPORT_MARGIN > window.innerWidth) {
    left = rect.left - PREVIEW_W - VIEWPORT_MARGIN;
  }
  if (left < VIEWPORT_MARGIN) {
    left = VIEWPORT_MARGIN;
  }
  let top = rect.top + rect.height / 2 - PREVIEW_H / 2;
  const maxTop = window.innerHeight - PREVIEW_H - VIEWPORT_MARGIN;
  top = Math.max(VIEWPORT_MARGIN, Math.min(top, maxTop));
  return { top, left };
}

export function CardThumbnail({ src, alt, thumbSizeClass }: Props) {
  const [pos, setPos] = useState<Pos | null>(null);
  const [imgFailed, setImgFailed] = useState(false);
  const thumbRef = useRef<HTMLDivElement>(null);
  const showImg = src && !imgFailed;

  const updatePos = useCallback(() => {
    const el = thumbRef.current;
    if (!el) return;
    setPos(computePos(el.getBoundingClientRect()));
  }, []);

  const onEnter = useCallback(() => {
    if (!showImg) return;
    updatePos();
  }, [showImg, updatePos]);

  const onLeave = useCallback(() => {
    setPos(null);
  }, []);

  useEffect(() => {
    if (pos === null) return;
    const onScrollOrResize = () => {
      const el = thumbRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > window.innerHeight) {
        setPos(null);
        return;
      }
      setPos(computePos(rect));
    };
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [pos]);

  return (
    <>
      <div
        ref={thumbRef}
        onMouseEnter={onEnter}
        onMouseLeave={onLeave}
        className={`relative ${thumbSizeClass} aspect-[5/7] shrink-0 rounded-lg overflow-hidden bg-zinc-900 border border-zinc-700 shadow-md cursor-zoom-in`}
      >
        {showImg ? (
          <img
            src={src}
            alt={alt}
            loading="lazy"
            onError={() => setImgFailed(true)}
            className="h-full w-full object-contain"
          />
        ) : (
          <div className="h-full w-full grid place-items-center text-zinc-600 text-xs">
            no image
          </div>
        )}
      </div>

      {pos !== null &&
        showImg &&
        createPortal(
          <div
            style={{ top: pos.top, left: pos.left, width: PREVIEW_W, height: PREVIEW_H }}
            className="fixed z-50 pointer-events-none rounded-xl overflow-hidden bg-zinc-900 border border-zinc-700 shadow-2xl ring-1 ring-black/40 animate-[fadeIn_120ms_ease-out]"
          >
            <img src={src} alt="" className="h-full w-full object-contain" />
          </div>,
          document.body,
        )}
    </>
  );
}
